import type { LiveNode } from './diff.js';
import { normalizeDeclaredInputReferences } from './input-refs.js';
import {
  authoredActionFields,
  authoredActionTitle,
  loginCredentialRef,
  mcpApplicationRef,
  mcpPersonaRef,
} from './authored-fields.js';
import { arr, blockquote, inputValue, num, obj, prop, str } from './pull-values.js';

export function renderJourneySection(
  sorted: LiveNode[],
  authLogin: LiveNode | undefined,
  inputNames: ReadonlySet<string>
): string[] {
  const lines = ['## Journey', ''];
  for (const node of sorted) {
    if (node === authLogin) continue; // represented by `actor` frontmatter
    const block = renderNode(node, inputNames);
    if (block.length === 0) continue;
    block[0] = `${block[0]}   <!-- @id ${node.id} -->`;
    lines.push(...block);
    lines.push('');
  }
  return lines;
}

/** Render a node to its `.canary.md` lines (lead-in first). */
function renderNode(node: LiveNode, inputNames: ReadonlySet<string>): string[] {
  const c = node.configJson ?? {};
  const inputRef = (value: string): string => normalizeDeclaredInputReferences(value, inputNames);
  switch (node.nodeType) {
    case 'login': {
      // A non-leading login is a mid-journey role switch. The credential ref
      // lives inside the bold lead-in. We emit the explicit `credential <name>`
      // prefix when the live node is bound to a static credential, so the
      // parser round-trips it back to `{ kind: 'credential' }`; otherwise we
      // emit the bare template name for backward compatibility.
      return [`**Switch role to ${renderLoginRef(c)}**`];
    }
    case 'navigate':
      return [`**Navigate to** \`${inputRef(str(c.pageUrl) ?? '/')}\`.`];
    case 'action': {
      const title = inputRef(authoredActionTitle(c));
      const out = [`**Action — ${title}**`];
      const desc = str(c.actionDescription);
      if (desc && inputRef(desc) !== title) out.push(blockquote(inputRef(desc)));
      for (const f of authoredActionFields(c)) {
        const typeSuffix = f.type && f.type !== 'text' ? ` (${f.type})` : '';
        // Quote field names that aren't bare identifiers (spaces, punctuation).
        const name = /^[A-Za-z0-9_-]+$/.test(f.name) ? f.name : `"${f.name}"`;
        out.push(`- field ${name}${typeSuffix}: ${inputValue(inputRef(f.value))}`);
      }
      for (const cap of arr(c.declaredOutputs)) {
        if (typeof cap === 'string') out.push(`- capture: ${cap}`);
      }
      for (const a of arr(c.inlineAssertions)) {
        const cond = str(prop(a, 'condition'));
        if (!cond) continue;
        const sev = str(prop(a, 'severity')) ?? 'fail';
        const flags = prop(a, 'agentic') === true ? `${sev}, agentic` : sev;
        out.push(`- assert (${flags}): ${inputRef(cond)}`);
      }
      if (c.captureAudio === true) {
        out.push('- captureAudio: true');
        if (str(c.captureLabel)) out.push(`- captureLabel: ${str(c.captureLabel)}`);
        if (num(c.captureExtraMs) !== undefined)
          out.push(`- captureExtraMs: ${num(c.captureExtraMs)}`);
      }
      if (c.alwaysUseAgent === true) out.push('- alwaysUseAgent: true');
      return out;
    }
    case 'assertion': {
      const cond = inputRef(str(c.condition) ?? '?');
      const out = [`**Assert** ${cond}`];
      const desc = str(c.description);
      if (desc && inputRef(desc) !== cond) out.push(blockquote(inputRef(desc)));
      // Map legacy strict:true → fail so the severity isn't silently weakened.
      const severity = str(c.severity) ?? (c.strict === true ? 'fail' : 'warn');
      out.push(`- severity: ${severity}`);
      if (str(c.expectedOutcome)) out.push(`- expectedOutcome: ${str(c.expectedOutcome)}`);
      if (c.alwaysUseAgent === true) out.push('- alwaysUseAgent: true');
      return out;
    }
    case 'seed': {
      const name = str(c.seedWorkflowName) ?? '?';
      const out = [`**Seed** \`${name}\`.`];
      // Canonical keys (snapshotVersion/onFailure/executionMode), with legacy
      // alias fallback for older nodes.
      const snap = c.snapshotVersion ?? c.seedSnapshotVersion;
      if (typeof snap === 'number') out.push(`- version: ${snap}`);
      const onFailure = str(c.onFailure) ?? str(c.seedOnFailure);
      if (onFailure) out.push(`- onFailure: ${onFailure}`);
      const mode = str(c.executionMode) ?? str(c.seedExecutionMode);
      if (mode) out.push(`- mode: ${mode}`);
      if (str(c.scope)) out.push(`- scope: ${str(c.scope)}`);
      if (typeof c.runTeardown === 'boolean') out.push(`- runTeardown: ${c.runTeardown}`);
      if (c.inputValues && typeof c.inputValues === 'object') {
        for (const [key, value] of Object.entries(c.inputValues as Record<string, unknown>)) {
          if (typeof value === 'string') out.push(`- input.${key}: ${value}`);
        }
      }
      return out;
    }
    case 'setup': {
      const name = str(c.setupFlowName) ?? '?';
      const out = [`**Setup** \`${name}\`.`];
      if (typeof c.snapshotVersion === 'number') out.push(`- version: ${c.snapshotVersion}`);
      if (str(c.executionMode)) out.push(`- mode: ${str(c.executionMode)}`);
      return out;
    }
    case 'wait': {
      const secs = num(c.delaySeconds) ?? num(c.waitDuration) ?? 0;
      return [`**Wait** ${secs}s.`];
    }
    case 'condition': {
      // Flat: the real branch nodes are sibling nodes with their own @id.
      // Single-line expression lives inside the bold lead-in (`**If <expr>**`);
      // a multi-line expression goes in a blockquote under a bare `**If**`.
      const expr =
        str(c.conditionDescription) ?? str(c.conditionExpression) ?? str(c.description) ?? '?';
      const normalized = inputRef(expr);
      return normalized.includes('\n')
        ? ['**If**', blockquote(normalized)]
        : [`**If ${normalized}**`];
    }
    case 'notification': {
      const channel = str(c.channel) ?? 'email';
      const to = inputRef(str(c.to) ?? '?');
      const out = [`**Notify ${channel}: ${to}**`];
      if (str(c.body)) out.push(blockquote(inputRef(str(c.body)!)));
      if (str(c.subject)) out.push(`- subject: ${inputRef(str(c.subject)!)}`);
      if (str(c.attachmentRef)) out.push(`- attach: ${str(c.attachmentRef)}`);
      return out;
    }
    case 'audio': {
      const ref = str(c.attachmentRef) ?? '?';
      const out = [`**Play audio** \`${ref}\``];
      if (c.loop === true) out.push('- loop: true');
      if (c.waitForCompletion === false) out.push('- waitForCompletion: false');
      if (str(c.label)) out.push(`- label: ${str(c.label)}`);
      return out;
    }
    case 'audio-assertion': {
      const det = obj(c.deterministic);
      const nl = obj(c.naturalLanguage);
      const prompt = str(nl?.prompt) ?? str(c.prompt);
      const model = str(nl?.model) ?? str(c.model);
      const out = [`**Assert audio** ${str(c.label) ?? '?'}`];
      out.push(`- severity: ${str(c.severity) ?? 'fail'}`);
      if (prompt) out.push(`- prompt: ${inputRef(prompt)}`);
      if (model) out.push(`- model: ${model}`);
      for (const [cfgKey, bulletKey] of [
        ['minDurationMs', 'minDurationMs'],
        ['maxDurationMs', 'maxDurationMs'],
        ['minRmsDb', 'minRmsDb'],
        ['silenceFractionMax', 'silenceFractionMax'],
        ['envelopeCorrelationMin', 'envelopeCorrelationMin'],
      ] as const) {
        const value = num(det?.[cfgKey]) ?? num(c[cfgKey]);
        if (value !== undefined) out.push(`- ${bulletKey}: ${value}`);
      }
      if (str(c.referenceAttachmentRef)) out.push(`- reference: ${str(c.referenceAttachmentRef)}`);
      return out;
    }
    case 'api_sequence': {
      const title = inputRef(str(c.customActionTitle) ?? str(c.title) ?? 'API sequence');
      const out = [`**API sequence — ${title}**`];
      if (str(c.apiEndpointContext)) out.push(blockquote(inputRef(str(c.apiEndpointContext)!)));
      if (str(c.credentialInputName)) out.push(`- credential: ${str(c.credentialInputName)}`);
      // Sidecar reference written by the CLI's pull flow when it fetches the
      // `/api-sequences/by-workflow/:id` config and stages it next to the spec.
      if (str(c.__apiSequenceSidecar)) {
        out.push(`- apiSequence: ${str(c.__apiSequenceSidecar)}`);
      }
      return out;
    }
    case 'sandbox_action': {
      const slug = str(c.actionSlug) ?? '?';
      const out = [`**Sandbox action — ${slug}**`];
      if (str(c.description)) out.push(blockquote(inputRef(str(c.description)!)));
      if (str(c.actionLabel)) out.push(`- label: ${str(c.actionLabel)}`);
      const params = obj(c.params);
      if (params) {
        for (const [key, value] of Object.entries(params)) {
          if (typeof value === 'string') out.push(`- param.${key}: ${value}`);
        }
      }
      const outputKeys = Array.isArray(c.outputKeys)
        ? c.outputKeys.filter((k): k is string => typeof k === 'string')
        : [];
      if (outputKeys.length > 0) out.push(`- outputs: ${outputKeys.join(', ')}`);
      return out;
    }
    case 'mcp': {
      const title = inputRef(str(c.title) ?? 'MCP');
      const out = [`**MCP — ${title}**`];
      // Names render name-first with id fallback (shared helpers with the
      // node hash), matching how login renders its credential binding.
      out.push(`- application: ${mcpApplicationRef(c) ?? '?'}`);
      const persona = mcpPersonaRef(c);
      if (persona) out.push(`- persona: ${persona}`);
      const outputs = Array.isArray(c.declaredOutputs)
        ? c.declaredOutputs.filter((k): k is string => typeof k === 'string')
        : [];
      if (outputs.length > 0) out.push(`- outputs: ${outputs.join(', ')}`);
      const tools = Array.isArray(c.toolAllowlist)
        ? c.toolAllowlist.filter((t): t is string => typeof t === 'string')
        : [];
      if (tools.length > 0) out.push(`- tools: ${tools.join(', ')}`);
      // Locked format: instructions blockquote after the bullets.
      if (str(c.instructions)) out.push(blockquote(inputRef(str(c.instructions)!)));
      return out;
    }
    case 'end':
      return [str(c.outcome) === 'fail' ? '**End (fail)**.' : '**End**.'];
    default:
      return [];
  }
}

/**
 * Format a login's identity for the `**Switch role to <ref>**` lead-in.
 * Template-bound logins render as the bare role name (backward compatible);
 * from_seed logins render as `seed-output <dataKey>` so the dedicated syntax
 * round-trips faithfully; static-credential logins render with the explicit
 * `credential <name>` prefix so the parser reads them back correctly.
 */
function renderLoginRef(c: Record<string, unknown>): string {
  if (c.credentialSource === 'from_seed') {
    const seedRef = c.seedCredentialRef as Record<string, unknown> | undefined;
    const ref =
      (typeof seedRef?.dataKey === 'string' && seedRef.dataKey) ||
      (typeof seedRef?.name === 'string' && seedRef.name);
    if (ref) return `seed-output ${ref}`;
  }
  const role = str(c.credentialTemplateName);
  if (role) return role;
  const cred = loginCredentialRef(c);
  if (cred) return `credential ${cred}`;
  return '?';
}
